import {
  type LatestQuarterlyProps,
  type UpcomingEventProps,
  Quarterly,
  SpecialUpcomingEvent,
  UpcomingEvent,
} from '@/components/investors/HomeEvents';
import { PricingWidget } from '@/components/investors/PricingWidget';
import { MediumSmallTitle, SmallUppercase, Title } from '@/components/investors/shared';
import { SeoProps } from '@/components/shared/SEO';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { Client } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { InstagramEmbed, LinkedInEmbed, XEmbed, YouTubeEmbed } from 'react-social-media-embed';
import styled, { createGlobalStyle } from 'styled-components';

const GlobalStyle = createGlobalStyle`
  .youtube-iframe {
    position: relative;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    height: 0;
    overflow: hidden;
    max-width: 100%;
  }

  .youtube-iframe iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const StyledMainPageTitle = styled(Title)`
  ${MEDIA_QUERIES.desktop`
    font-size: 4.2rem;
    line-height: 5.2rem;
  `}
`;

const ViewAllQuarter = styled(Link)`
  ${font('main', { fontSize: '1.4rem', lineHeight: '2rem', fontWeight: 400 })}
  color: ${themedColor('primary')};
  width: fit-content;
`;

const HomeEventsWrapper = styled(Flex)`
  gap: 12px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    gap: 8px;
  `}
`;

const FeedContentWrapper = styled(Flex)`
  ${font('main', { lineHeight: '145%' })}
`;

const FeedArticleContent = styled.div`
  ${font('main', { lineHeight: '145%' })};
  ul {
    list-style-type: disc;
    margin-left: 16px;
    margin-block: 10px;
    li {
      margin-bottom: 8px;
    }
  }
`;

const FeedArticle: React.FC<
  React.PropsWithChildren<{
    link: {
      url: string;
      label: string;
    };
    image?: {
      url: string;
      alt?: string;
    };
  }>
> = ({ children, link, image }) => {
  return (
    <Flex flexDirection="column" gap="12px" style={{ borderBottom: '1px solid #E0E0E0', paddingBottom: '20px' }}>
      <FeedArticleContent>{children}</FeedArticleContent>
      {image?.url && (
        <Image
          src={image.url}
          alt={image?.alt ?? 'Lemonade Feed Image'}
          width={550}
          height={300}
          layout="responsive"
          style={{ borderRadius: '10px', border: '1px solid #E0E0E0', marginBlock: '10px' }}
        />
      )}
      {link.label && (
        <a
          style={{ display: 'inline-block', width: 'fit-content', fontSize: '1.4rem', lineHeight: '2rem' }}
          href={link.url}
        >
          {link.label}
        </a>
      )}
    </Flex>
  );
};

type FeedPresentation = 'x' | 'instagram' | 'linkedin' | 'youtube' | 'facebook';

type Feed =
  | {
      type: 'post';
      fields: {
        date: string;
        content: string;
        link: {
          label: string;
          url: string; // relative ex. /news/my-news-slug
        };
        image?: {
          url: string;
          alt?: string;
        };
      };
    }
  | {
      type: 'social';
      fields: {
        date: string;
        content?: string; // optional for social feeds
        presentation: FeedPresentation;
        embed_url: string;
        height?: number; // mandatory for linkedin
        image?: {
          url: string;
          alt?: string;
        };
      };
    };

interface HomePageProps {
  feeds: Feed[];
  latest_quarterly: LatestQuarterlyProps | null;
  upcoming_event: UpcomingEventProps | null;
}

const MAX_WIDTH = 550;

const SOCIAL_EMBEDS = {
  x: XEmbed,
  instagram: InstagramEmbed,
  linkedin: LinkedInEmbed,
  youtube: YouTubeEmbed,
} as Record<
  FeedPresentation,
  React.ComponentType<{ url: string; height?: number | string; width?: string; style?: React.CSSProperties }>
>;

const SOCIAL_HEIGHT = {
  x: '100%',
  instagram: '100%',
  linkedin: '100%',
  youtube: 'auto',
  facebook: '100%',
} as Record<FeedPresentation, number | string>;

const seo = {
  title: 'Lemonade Inc. - Hey investors, welcome home!',
  description:
    'Explore Lemonade’s Investor Relations hub for news, financials, stock data, governance info, and resources for shareholders and analysts.',
} as SeoProps;

const TitleSection = () => {
  return (
    <>
      <StyledMainPageTitle>Welcome home.</StyledMainPageTitle>
      <Box display={['block', 'none']} mb={['20px', '0px']}>
        <PricingWidget />
      </Box>
    </>
  );
};

interface HiglightSectionProps {
  latest_quarterly: LatestQuarterlyProps | null;
  upcoming_event: UpcomingEventProps | null;
}

const useSpecialUpcomingEvent = ({ month, day, hour }: { month: number; day: number; hour: number }) => {
  const now = new Date();

  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Etc/GMT+4',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    hour12: false,
  };

  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(now);

  let current_month_tz = NaN;
  let current_day_tz = NaN;
  let current_hour_tz = NaN;

  parts.forEach(part => {
    if (part.type === 'month') current_month_tz = parseInt(part.value, 10);
    if (part.type === 'day') current_day_tz = parseInt(part.value, 10);
    if (part.type === 'hour') current_hour_tz = parseInt(part.value, 10);
  });

  if (isNaN(current_month_tz) || isNaN(current_day_tz) || isNaN(current_hour_tz)) {
    console.error('Failed to extract date/time parts in target timezone (UTC-4).');
    return false;
  }

  const initialHour = hour - 2;
  const finalHour = hour;

  const matcheTimeFrame = current_hour_tz >= initialHour && current_hour_tz <= finalHour;
  const matchDate = month === current_month_tz && day === current_day_tz;

  return matchDate && matcheTimeFrame;
};

const HiglightSection: React.FC<HiglightSectionProps> = ({ latest_quarterly, upcoming_event }) => {
  const showSpecialUpcomingEvent = useSpecialUpcomingEvent({
    month: 11,
    day: 5,
    hour: 8,
  });
  return (
    <Flex flexDirection="column" gap="12px">
      <HomeEventsWrapper
        flexDirection={['column', 'row', 'row']}
        mt="20px"
        width="100%"
        maxWidth={MAX_WIDTH}
        alignItems="stretch"
      >
        {showSpecialUpcomingEvent ? (
          <SpecialUpcomingEvent upcomingEvent={upcoming_event} hasUpcomingEvent={upcoming_event != null} />
        ) : (
          <UpcomingEvent upcomingEvent={upcoming_event} hasUpcomingEvent={upcoming_event != null} />
        )}
        <Quarterly latestQuarterly={latest_quarterly} hasUpcomingEvent={upcoming_event != null} />
      </HomeEventsWrapper>
      <ViewAllQuarter href="/investor/quarterly-results">View all quarters</ViewAllQuarter>
    </Flex>
  );
};

const Feed = ({ feeds }: { feeds: Feed[] }) => {
  return (
    <Flex flexDirection={['column', 'column', 'row']} gap="20px" mt="42px" justifyContent="space-between" width="100%">
      <Flex flexDirection="column" gap="20px" maxWidth={MAX_WIDTH} width="100%">
        <MediumSmallTitle>Lemonade Updates</MediumSmallTitle>

        {feeds.map((feed, index) => {
          if (feed.type === 'post') {
            return (
              <Flex flexDirection="column" gap="4px" key={feed.fields.link.url + index}>
                <SmallUppercase>{feed.fields.date}</SmallUppercase>
                <FeedArticle
                  link={{ url: feed.fields.link.url, label: feed.fields.link.label }}
                  image={feed.fields.image}
                >
                  {parse(feed.fields.content)}
                </FeedArticle>
              </Flex>
            );
          }

          const { presentation, embed_url, height, image } = feed.fields;
          const Social = SOCIAL_EMBEDS[presentation];

          return (
            <Flex
              flexDirection="column"
              gap="12px"
              key={embed_url + index}
              style={{ borderBottom: '1px solid #E0E0E0' }}
              pb={28}
            >
              <Flex flexDirection="column" gap="4px">
                <SmallUppercase>{feed.fields.date}</SmallUppercase>
                {image && (
                  <Image
                    src={image?.url}
                    alt={image?.alt ?? 'Lemonade Feed Image'}
                    width={550}
                    height={300}
                    layout="responsive"
                    style={{ borderRadius: '10px', border: '1px solid #E0E0E0', marginBlock: '10px' }}
                  />
                )}
                <FeedContentWrapper>{feed.fields.content && parse(feed.fields.content)}</FeedContentWrapper>
              </Flex>
              <Social
                url={embed_url}
                width="100%"
                height={height ?? SOCIAL_HEIGHT[presentation]}
                style={presentation === 'youtube' ? { borderRadius: '10px' } : {}}
              />
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

const ShortBioContainer = styled(Flex)`
  ${font('main', { lineHeight: '145%' })}
  p {
    text-wrap: pretty;
  }

  div a {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 1.4rem;
  }
`;

const DownloadIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} fill="none">
      <path
        stroke="#FF0083"
        strokeLinecap="round"
        strokeWidth={1.8}
        d="M12.952 9.82v2.26a1 1 0 0 1-1 1H4.048a1 1 0 0 1-1-1V9.82M8 2.57v6.202"
      />
      <path
        stroke="#FF0083"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M5 6.218 8 8.82l2.998-2.602"
      />
    </svg>
  );
};

const SHORT_BIO_DOWNLOAD_LINKS = [
  {
    url: 'https://assets.lemonade.com/docs/lemonade_investor_presentation.pdf',
    label: 'Investor Presentation',
  },
  {
    url: 'https://assets.lemonade.com/docs/lemonade_finance_dashboard.xlsx',
    label: 'Finance Dashboard',
  },
];

const ShortBio = () => {
  return (
    <ShortBioContainer
      flexDirection="column"
      gap="12px"
      mt={['36px', '30px']}
      mb={['12px', '22px']}
      maxWidth={MAX_WIDTH}
      width="100%"
    >
      <p>
        <b>Lemonade</b> delivers AI-powered renters, pet, car, homeowners, and life insurance across the US and EU. We
        process claims instantly, keep costs low, and donate unused premiums to causes our customers choose.
      </p>
      <Flex alignItems={['flex-start', 'center']} gap="16px" flexDirection={['column', 'row']}>
        {SHORT_BIO_DOWNLOAD_LINKS.map(link => (
          <a href={link.url} key={link.url} download target="_blank">
            <DownloadIcon />
            {link.label}
          </a>
        ))}
      </Flex>
    </ShortBioContainer>
  );
};

export default function HomePage({ feeds, latest_quarterly, upcoming_event }: HomePageProps) {
  const showHiglightSection = useMemo(() => {
    return latest_quarterly != null || upcoming_event != null;
  }, [latest_quarterly, upcoming_event]);
  return (
    <InvestorsPageTemplate seo={seo}>
      <GlobalStyle />
      <Box width="100%" px={[20, 20, 0]}>
        <TitleSection />
        <ShortBio />
        {showHiglightSection && <HiglightSection latest_quarterly={latest_quarterly} upcoming_event={upcoming_event} />}
        <Feed feeds={feeds} />
      </Box>
    </InvestorsPageTemplate>
  );
}

interface HomeData {
  latest_quarterly: LatestQuarterlyProps | null;
  feeds: Feed[];
  upcoming_event: UpcomingEventProps | null;
}

const getHomeData = async (): Promise<{ data: HomeData }> => {
  try {
    return await Client.get<{ data: HomeData }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/home`).then(data => data);
  } catch (err) {
    console.error('🔥 feeds error', err);
    return {
      data: {
        feeds: [],
        latest_quarterly: { year: '', quarter: '', links: [] },
        upcoming_event: { name: '', date: '', link: { url: '', label: '' } },
      },
    };
  }
};

export const getStaticProps = async () => {
  const {
    data: { feeds, latest_quarterly, upcoming_event },
  } = await getHomeData();

  return {
    props: {
      feeds,
      latest_quarterly,
      upcoming_event,
    },
    revalidate: 10, // 10 seconds, we need this to be wired as much as possible to the data source
  };
};
